import React, { useEffect, useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { useForm } from "react-hook-form";
import { religionColumns } from "../../../tables/religionColumns";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { can } from "../../../utils/can";

export default function Religion() {
  const PAGE_SIZE = 10;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [religions, setReligions] = useState([]);
  const [page, setPage] = useState(1);
  const [editId, setEditId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";

  /* ================= FETCH RELIGIONS ================= */
  const fetchReligions = async (pageNumber = 1, pSize = pageSize, currentSearch = activeSearch) => {
    setListLoading(true);

    try {
      let url =
        `${API_URL.replace(/\/$/, "")}/api/admin/religion` +
        `?page=${pageNumber}&limit=${pSize}` +
        `&sortBy=${sortBy}&sortOrder=${sortOrder}`;

      if (currentSearch && currentSearch.trim()) {
        url += `&search=${encodeURIComponent(currentSearch.trim())}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        setReligions(result.data || []);
        setTotalPages(result.totalPages || 1);
      } else {
        console.error(result.message || "Failed to fetch religions");
      }
    } catch (err) {
      console.error("Error fetching religions:", err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchReligions(page, pageSize, activeSearch);
  }, [page, pageSize, sortBy, sortOrder, activeSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchInput);
    fetchReligions(1, pageSize, searchInput);
  };

  const handleSearchReset = () => {
    setSearchInput("");
    setActiveSearch("");
    setPage(1);
    fetchReligions(1, pageSize, "");
  };

  /* ================= FORM SUBMIT ================= */
  const onSubmit = async (data) => {

    if (can("religion", "create") === false) {
      alert("You don't have permission.");
      return;
    }

    setSuccess("");
    setErrorMsg("");
    setLoading(true);

    try {
      const url = editId
        ? `${API_URL.replace(/\/$/, "")}/api/admin/religion/update/${editId}`
        : `${API_URL.replace(/\/$/, "")}/api/admin/religion/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: data.name.trim(),
          status: data.isActive ? "Active" : "Deactive",
          homePage: !!data.homePage,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(editId ? "Religion updated successfully!" : "Religion added successfully!");
        setErrorMsg("");
        reset();
        setEditId(null);
        fetchReligions(page, pageSize, activeSearch);
      } else {
        setErrorMsg(result.message || "Failed to save religion.");
      }
    } catch (err) {
      console.error("Error:", err);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = (religion) => {
    if (can("religion", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    setValue("name", religion.name);
    setValue("isActive", religion.status === "Active");
    setValue("homePage", !!religion.homePage);
    setEditId(religion._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= HOME PAGE TOGGLE ================= */
  const handleHomePageToggle = async (religion) => {
    if (can("religion", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/religion/update/${religion._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            homePage: !religion.homePage,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setReligions((prev) =>
          prev.map((item) =>
            item._id === religion._id
              ? { ...item, homePage: !religion.homePage }
              : item
          )
        );
      } else {
        setSuccess(result.message || "Failed to update Home Page.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (can("religion", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this religion?")) return;

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/religion/delete/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccess("Religion deleted successfully!");
        fetchReligions(page);
      } else {
        setSuccess(result.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const columns = religionColumns(handleEdit, handleDelete, handleHomePageToggle, PAGE_SIZE);

  return (
    <div class="container body">
      <div class="main_container">
        <Sidebar />
        <Header pageTitle="Religion" />

        <div className="right_col" role="main">


          <div className="x_panel">
            <div className="x_title">
              <h2>Religion Entry</h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                  <div className="col-12 col-sm-4 mb-3 ">
                    <label>Enter Religion</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      {...register("name", { required: "Please enter religion name" })}
                    />
                    {errors.name && (
                      <div className="text-danger mt-1">{errors.name.message}</div>
                    )}
                  </div>

                  <div
                    className="col-12 col-sm-2 text-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "38px",
                      marginTop: "24px",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="active"
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          margin: 0,
                          accentColor: "#8b205f",
                        }}
                        {...register("isActive")}
                      />
                      <label
                        htmlFor="active"
                        className="form-label mb-0"
                        style={{
                          cursor: "pointer",
                          margin: 0,
                          marginLeft: "8px",
                          fontWeight: 500,
                          userSelect: "none",
                        }}
                      >
                        Active/Deactive
                      </label>
                    </div>
                  </div>

                  <div
                    className="col-12 col-sm-2 text-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "38px",
                      marginTop: "24px",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="homePage"
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          margin: 0,
                          accentColor: "#8b205f",
                        }}
                        {...register("homePage")}
                      />
                      <label
                        htmlFor="homePage"
                        className="form-label mb-0"
                        style={{
                          cursor: "pointer",
                          margin: 0,
                          marginLeft: "8px",
                          fontWeight: 500,
                          userSelect: "none",
                        }}
                      >
                        Home Page
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-sm-4 text-right" style={{ marginTop: "24px" }}>
                    <button type="submit" className="btn sitebtn" disabled={loading} style={{ minHeight: "38px" }}>
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                    {success && <div className="text-success mt-1">{success}</div>}
                    {errorMsg && <div className="text-danger mt-1">{errorMsg}</div>}
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="x_panel">
            <div className="x_title">
              <h2>Religion List</h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search religion..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <button className="btn sitebtn mb-0" type="submit" style={{ borderRadius: "0 4px 4px 0" }}>
                        <i className="fa fa-search" /> Search
                      </button>
                      {(activeSearch || searchInput) && (
                        <button
                          className="btn btn-secondary mb-0 ms-1 ml-1"
                          type="button"
                          onClick={handleSearchReset}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              <AdminDataTable
                columns={columns}
                data={religions}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
